import { Component, Injector, Input, OnInit } from '@angular/core';
import { ExcelExportGridActionHandler } from '../../../../../components/common/grid-base/actions/excel-export-grid-action-handler';
import { GridActionHandlerBase } from '../../../../../components/common/grid-base/actions/grid-action-handler-base';
import { LoadDataGridActionHandler } from '../../../../../components/common/grid-base/actions/load-data-grid-action-handler';
import { LoadDataKeepFilterKeepSelectionGridActionHandler } from '../../../../../components/common/grid-base/actions/load-data-keep-filter-keep-selection-grid-action-handler';
import { TabConfig } from '../../../../../components/common/pollux-tab-config/pollux-form-config';
import { WorkplaceBase } from '../../../../../components/common/workplace-base/workplace-base';
import { WorkspaceBase } from '../../../../../components/common/workspace-base/workspace-base';
import { PropertiesGridComponent } from '../../grids/properties-grid/properties-grid.component';
import { AssignPropertiesToPortfolioGridActionHandler } from './actions/assign-properties-to-portolio-grid-action-handler';
import { SearchPropertiesLinkToPortfolioGridActionHandler } from './actions/search-properties-link-to-portfolio-grid-action';
import { AppStorageService } from '../../../../../services/app-storage.service';
import { take } from 'rxjs';
import { UnselectCheckboxesGridActionHandler } from '../../../../../components/common/grid-base/actions/unselect-checkboxes-grid-action-handler';

/**
 * Properties workspace
 */
@Component({
  selector: 'properties-workspace',
  templateUrl: './properties-workspace.component.html',
  styleUrls: ['./properties-workspace.component.scss']
})
export class PropertiesWorkspaceComponent extends WorkspaceBase implements OnInit {
  /**
   * JavaScript Oid property name of BO (in this case Property DTO)
   */
  public oidPropertyName = "Oid";

  /**
   * Show the form
   * @param oid Oid of the item that should be loaded in the form. Use -1 for new mode
   */
  public showForm(oid: number): void {
    this.showGrid = false;
    this.oid = oid;
  }

  /**
   * The workplace
   */
  private _workplace: WorkplaceBase;

  /**
   * Sets the workplace
   */
  @Input() public set workplace(workplace: WorkplaceBase) {
    this._workplace = workplace;
  }

  /**
   * Gets the workplace
   */
  public get workplace(): WorkplaceBase {
    return this._workplace;
  }

  /**
   * The tab element
   */
  private _tabElement: TabConfig;

  /**
   * Sets te tab element
   */
  @Input() public set tabElement(tabElement: TabConfig) {
    this._tabElement = tabElement;
  }

  /**
   * Gets the tab element
   */
  public get tabElement(): TabConfig {
    return this._tabElement;
  }

  /**
   * Assign to calc model project ID
   */
  public assignCalculationModelProjectId = 0;

  /**
   * Assigned templates
   */
  public assignedPropertiesTemplates = [];

  /**
   * Constructor
   * @param injector
   * @param appStorage
   */
  constructor(protected injector: Injector, private appStorage: AppStorageService) {
    super(injector);
  }

  /**
   * Called if the grid state was changed. Use this to notify the nav bar or perform similar actions
   * @param gridState The new grid state: true: Grid is shown, so the form isn't shown. False: Grid isn't show, but the form is shown
   */
  protected onShowGridChanged(gridState: boolean): void {
    this.eventBus.broadcastEvent('showGridChanged', { area: 'projectValuationProperty', value: gridState });
  }

  /**
   * Gets the action handlers
   */
  protected get actionHandlers(): GridActionHandlerBase[] {
    return [
      new ExcelExportGridActionHandler(this.injector, this, 'exportToExcelPropertiesGrid'),
      new LoadDataGridActionHandler(this.injector, this, 'PropertyLoadData'),
      new LoadDataGridActionHandler(this.injector, this, 'valuationProjectReportItemSaved'),
      new LoadDataKeepFilterKeepSelectionGridActionHandler(this.injector, this, 'valuationPropertyItemSaved'),
      new LoadDataKeepFilterKeepSelectionGridActionHandler(this.injector, this, 'propertiesDataSaved'),

      new AssignPropertiesToPortfolioGridActionHandler(this.injector, this, 'property:assignPropertiesToPortolio'),
      new SearchPropertiesLinkToPortfolioGridActionHandler(this.injector, this, 'buttonSearchPropertiesForPortfolio', this.appStorage),
      new UnselectCheckboxesGridActionHandler(this.injector, this)
    ];
  }

  /**
   * Registers any required events. Please don't forget to unsubscibe!
   */
  protected registerEvents(): void {
    const onEvents = this.onEvents;

    onEvents.push(this.eventBus.subscribeEvent('closeCompPropertiesForm', (_payload, _options) => {
      this.showGrid = true;
      this.oid = null;
      this.eventBus.broadcastEvent('showSaveCancelNavigationAction', false);
    }));

    onEvents.push(this.eventBus.subscribeEvent('backClickedTrue', (_payload, _options) => { this.showGrid = true; }));

    onEvents.push(this.eventBus.subscribeEvent('valuationTabSelect', (tab, _options) => {
      if (tab !== 'tabProperty') {
        this.assignCalculationModelProjectId = 0;
      }
    }));

    onEvents.push(this.eventBus.subscribeEvent('properties:assignCalculationModelTemplate', (selectedProject, _options) => {
      const tabConfig = this.workplace.tabConfig;
      const filtersOverride = {
        logic: "or",
        filters: [
          { field: "OccursInHistoricalProjects", operator: "contains", value: selectedProject.Oid }
        ]
      };

      this.filterOverride(selectedProject, 0, tabConfig, false, filtersOverride);
      setTimeout(() => {
        this.assignCalculationModelProjectId = selectedProject.Oid;
      });
    }));

    onEvents.push(this.eventBus.subscribeEvent('properties:calculationModelTemplateAssigned', (projectId, _options) => {
      // templates are assigned to some of project properties => refresh template names
      this.loadAssignedPropertiesTemplates(projectId).then(() => {
        const properties = this.grid.currentPageItems;
        this.processAssignedCalculationModelTemplates(properties);
        this.grid.applyFilter(this.grid.state.filter, null);
      });
    }));
  }

  /**
   * Applies a filter if an element was selected in a higher hierarchy area
   * @param _filter Filter settings
   * @param _selectedHierarchy Hierarchy where the element was selected
   * @param _tabConfig Tab config
   * @param reset Reset the selection in the current area
   */
  public filter(item: any, selectedHierarchy: number, tabConfig: any, reset: boolean): void {
    this.filterOverride(item, selectedHierarchy, tabConfig, reset, null);
  }

  /**
   * Applies a filter if an element was selected in a higher hierarchy area
   * @param _filter Filter settings
   * @param _selectedHierarchy Hierarchy where the element was selected
   * @param _tabConfig Tab config
   * @param reset Reset the selection in the current area
   */
  public filterOverride(item: any, selectedHierarchy: number, tabConfig: any, reset: boolean, filtersOverride: any): void {
    const grid = this.grid as PropertiesGridComponent;
    this.assignCalculationModelProjectId = 0;
    this.assignedPropertiesTemplates = [];
    grid.filterByValuationPortfolioId = null;

    if (reset) {
      this.grid.loadData({
        skip: 0
      }, null);
    } else {
      const filteredTab = tabConfig[selectedHierarchy];
      switch (filteredTab?.searchMode) {
        case 'projectstd': {
          const projectId = item.Oid;
          const filters = {
            logic: "or",
            filters: [
              { field: "OccursInHistoricalProjects", operator: "containsInCsv", value: projectId }
            ]
          };

          this.loadAssignedPropertiesTemplates(projectId).then(() => {
            grid.applyFilter(filtersOverride || filters);
          });

          break;
        }
        case 'valuationPortfolio': {
          const projectId = item.ProjectId || item.ProjectID;
          const valuationPortfolioId = item.Oid;

          grid.filterByValuationPortfolioId = valuationPortfolioId;
          this.loadAssignedPropertiesTemplates(projectId).then(() => {
            grid.applyFilter(null);
          });

          break;
        }
        case 'report': {
          const reportId = item.Oid;
          const newFilter = {
            logic: "or",
            filters: [{ field: "OccursInReports", operator: "containsInCsv", value: reportId }]
          };

          grid.applyFilter(newFilter);
          break;
        }
      }
    }
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.init();
    this.workplace.onTabCreated(this);
  }

  /**
   * Loads assigned import templates
   * @param projectId
   * @returns
   */
  public loadAssignedPropertiesTemplates(projectId): Promise<void> {
    return new Promise<void>((resolve) => {
      //load templates assigned to properties on project level
      const url = this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `Properties/assignedCalculationModelTemplates?projectId=${projectId}`)
      this.http.get(url).pipe(take(1)).subscribe((result: any[]) => {
        this.assignedPropertiesTemplates = result;
        resolve();
      });
    });
  }

  /**
   * Processes the assigned import templates
   * @param properties
   */
  public processAssignedCalculationModelTemplates(properties) {
    for (const row of properties) {
      const template = this.assignedPropertiesTemplates.find(t => t.oid === row.Oid);
      row.assignedTemplateName = template ? template.description : '';
    }
  }
}
